import { useState, useCallback, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import NumberBall from '@/components/base/NumberBall';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { validateOrFallback, validateLottoCombination } from '@/lib/lottoCombination';
import { generateFixedExcludeRandom } from '@/lib/numberGenerators';
import QRShare from '@/components/feature/QRShare';
import Reveal from '@/components/base/Reveal';
import ResponsibleNotice from '@/components/feature/ResponsibleNotice';

const ALL_NUMBERS = Array.from({ length: 45 }, (_, i) => i + 1);
const MAX_EXCLUDE = 5;
const MAX_FIXED = 3;

const RandomGenerator: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [numbers, setNumbers] = useState<number[]>([]);
  const [bonus, setBonus] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [saveError, setSaveError] = useState('');
  const [qrModal, setQrModal] = useState(false);

  // Fixed/Excluded numbers
  const [excludedNumbers, setExcludedNumbers] = useState<number[]>([]);
  const [fixedNumbers, setFixedNumbers] = useState<number[]>([]);
  const [showOptions, setShowOptions] = useState(false);
  const [selectionMode, setSelectionMode] = useState<'exclude' | 'fixed'>('exclude');

  const toggleNumber = (num: number) => {
    if (selectionMode === 'exclude') {
      setExcludedNumbers((prev) => {
        if (prev.includes(num)) return prev.filter((n) => n !== num);
        if (prev.length >= MAX_EXCLUDE) return prev;
        return [...prev, num].sort((a, b) => a - b);
      });
      // Remove from fixed if excluded
      setFixedNumbers((prev) => prev.filter((n) => n !== num));
    } else {
      setFixedNumbers((prev) => {
        if (prev.includes(num)) return prev.filter((n) => n !== num);
        if (prev.length >= MAX_FIXED) return prev;
        return [...prev, num].sort((a, b) => a - b);
      });
      // Remove from excluded if fixed
      setExcludedNumbers((prev) => prev.filter((n) => n !== num));
    }
  };

  const generateNumbers = useCallback(() => {
    setIsGenerating(true);
    setShowResult(false);
    setNumbers([]);
    setBonus(null);
    setSaved(false);
    setSaveLabel('');

    setTimeout(() => {
      const generated = generateFixedExcludeRandom(fixedNumbers, excludedNumbers);
      const validated = validateOrFallback(generated.numbers, generated.bonus);
      setNumbers(validated.numbers);
      setBonus(validated.bonus);
      setIsGenerating(false);
      setShowResult(true);
    }, 600);
  }, [excludedNumbers, fixedNumbers]);

  const handleSave = async () => {
    if (saved || numbers.length !== 6 || bonus === null) return;

    if (!user) {
      navigate('/login');
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      // 저장 직전에는 fail-closed: validator 통과 시에만 DB 저장한다.
      // 유효하지 않으면 화면 번호를 다른 랜덤 번호로 교체하지 않고 insert 도 하지 않는다.
      const validation = validateLottoCombination(numbers, bonus);
      if (!validation.valid) {
        setSaveError('생성된 번호가 유효하지 않습니다. 번호를 다시 생성해주세요.');
        return;
      }
      const validated = validation.combination; // 검증 통과 시 반환된 정렬된 조합 저장
      // 주 데이터 저장: 실제 생성 행을 확인해 실패를 성공으로 표시하지 않는다
      const { data: inserted, error: insertError } = await supabase
        .from('saved_numbers')
        .insert({
          user_id: user.id,
          numbers: validated.numbers,
          bonus: validated.bonus,
          generator_type: 'random',
          label: saveLabel.trim() || null,
        })
        .select('id')
        .single();

      if (insertError || !inserted) {
        if (import.meta.env.DEV) {
          console.error('[RandomGenerator] 번호 저장 실패:', insertError?.message ?? 'no row');
        }
        setSaveError('번호를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      // 주 데이터 저장 성공 → 성공 UI 표시
      setSaved(true);

      // 보조 활동 로그: 실패해도 저장 성공을 뒤집지 않는다(중복 저장 방지)
      try {
        const { error: logError } = await supabase.from('user_activity_logs').insert({
          user_id: user.id,
          action: 'generate_random',
          detail: {},
        });
        if (logError && import.meta.env.DEV) {
          console.error('[RandomGenerator] 활동 로그 기록 실패:', logError.message);
        }
      } catch (logErr) {
        if (import.meta.env.DEV) {
          console.error('[RandomGenerator] 활동 로그 기록 예외:', logErr);
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[RandomGenerator] 번호 저장 예외:', err);
      }
      setSaveError('번호를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  const getNumClass = (num: number) => {
    if (excludedNumbers.includes(num)) return 'bg-accent-100 text-accent-600 border-2 border-accent-400';
    if (fixedNumbers.includes(num)) return 'bg-primary-100 text-primary-700 border-2 border-primary-400';
    if (selectionMode === 'exclude') return 'bg-background-100 text-foreground-500 hover:bg-accent-50 hover:text-accent-600';
    return 'bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-600';
  };

  const cannotGenerate = fixedNumbers.length > 6;

  return (
    <section className="w-full">
      <Reveal className="w-full max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-3">
            랜덤 번호 생성기
          </h2>
          <p className="text-foreground-500 text-base md:text-lg">
            버튼 하나로 행운의 6개 번호를 뽑아보세요
          </p>
        </div>

        {/* Options toggle */}
        <div className="mb-4 flex justify-center">
          <button
            onClick={() => setShowOptions(!showOptions)}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              showOptions
                ? 'bg-primary-100 text-primary-700'
                : 'border border-background-300 text-foreground-500 hover:bg-background-100'
            }`}
          >
            <i className="ri-settings-3-line"></i>
            고급 옵션
            {(excludedNumbers.length > 0 || fixedNumbers.length > 0) && (
              <span className="inline-flex w-5 h-5 rounded-full bg-accent-500 text-accent-950 text-[10px] items-center justify-center font-bold">
                {excludedNumbers.length + fixedNumbers.length}
              </span>
            )}
          </button>
        </div>

        {/* Number selection grid */}
        {showOptions && (
          <div className="mb-6 p-5 rounded-2xl bg-background-100 border border-background-200">
            {/* Mode switch */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex bg-background-200 rounded-full p-1">
                <button
                  onClick={() => setSelectionMode('exclude')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectionMode === 'exclude'
                      ? 'bg-background-50 text-accent-700 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  <i className="ri-close-circle-line mr-1"></i>
                  제외수 {excludedNumbers.length}/{MAX_EXCLUDE}
                </button>
                <button
                  onClick={() => setSelectionMode('fixed')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectionMode === 'fixed'
                      ? 'bg-background-50 text-primary-700 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  고정수 {fixedNumbers.length}/{MAX_FIXED}
                </button>
              </div>
              {(excludedNumbers.length > 0 || fixedNumbers.length > 0) && (
                <button
                  onClick={() => { setExcludedNumbers([]); setFixedNumbers([]); }}
                  className="text-xs text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer ml-auto"
                >
                  초기화
                </button>
              )}
            </div>

            {/* Selection info */}
            <div className="flex items-center gap-4 mb-3">
              {excludedNumbers.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-foreground-400">제외:</span>
                  {excludedNumbers.map((n) => (
                    <span key={n} className="inline-flex w-5 h-5 rounded-full bg-accent-100 text-accent-700 text-[10px] items-center justify-center font-bold">
                      {n}
                    </span>
                  ))}
                </div>
              )}
              {fixedNumbers.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-foreground-400">고정:</span>
                  {fixedNumbers.map((n) => (
                    <span key={n} className="inline-flex w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[10px] items-center justify-center font-bold">
                      {n}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Number grid */}
            <div className="grid grid-cols-7 sm:grid-cols-15 gap-1.5">
              {ALL_NUMBERS.map((num) => (
                <button
                  key={num}
                  onClick={() => toggleNumber(num)}
                  disabled={
                    (selectionMode === 'exclude' && fixedNumbers.includes(num)) ||
                    (selectionMode === 'fixed' && excludedNumbers.includes(num))
                  }
                  className={`w-full h-10 rounded-lg text-xs font-bold transition-all cursor-pointer ${getNumClass(num)} ${
                    (selectionMode === 'exclude' && fixedNumbers.includes(num)) ||
                    (selectionMode === 'fixed' && excludedNumbers.includes(num))
                      ? 'opacity-30 cursor-not-allowed'
                      : ''
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>

            {cannotGenerate && (
              <div className="mt-3 p-2 rounded-lg bg-accent-50 border border-accent-100 text-xs text-accent-800">
                <i className="ri-error-warning-line mr-1"></i>
                고정수가 6개를 초과했습니다. 최대 3개까지 선택 가능합니다.
              </div>
            )}
          </div>
        )}

        <div className="bg-background-100 rounded-2xl p-8 md:p-12 mb-8 min-h-[180px] flex flex-col items-center justify-center border border-background-200">
          {!showResult && !isGenerating && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-shuffle-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-400 text-sm">
                아래 버튼을 눌러 번호를 생성해보세요
              </p>
            </div>
          )}

          {isGenerating && (
            <div className="flex flex-col items-center gap-4">
              <div className="flex gap-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="w-14 h-14 rounded-full bg-background-300 animate-pulse"
                    style={{ animationDelay: `${i * 80}ms` }}
                  />
                ))}
              </div>
              <p className="text-foreground-500 text-sm mt-2">번호 생성 중...</p>
            </div>
          )}

          {showResult && numbers.length === 6 && bonus !== null && (
            <div className="flex flex-col items-center gap-6">
              <div className="flex flex-wrap items-center justify-center gap-3">
                {numbers.map((num, idx) => (
                  <NumberBall
                    key={num}
                    number={num}
                    size="lg"
                    animated
                    animationDelay={idx * 100}
                    highlight={fixedNumbers.includes(num)}
                  />
                ))}
                <div className="flex items-center justify-center w-8 h-14">
                  <span className="text-foreground-400 text-xl font-bold">+</span>
                </div>
                <NumberBall
                  number={bonus}
                  isBonus
                  size="lg"
                  animated
                  animationDelay={600}
                />
              </div>
              <p className="text-foreground-400 text-xs mt-1">보너스 번호</p>

              {/* Save area */}
              <div className="flex flex-col items-center gap-3 mt-4 pt-4 border-t border-background-200 w-full max-w-sm">
                {saved ? (
                  <div className="flex items-center gap-2 text-secondary-600">
                    <i className="ri-check-double-line"></i>
                    <span className="text-sm font-medium">저장 완료!</span>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={saveLabel}
                      onChange={(e) => setSaveLabel(e.target.value)}
                      placeholder="라벨 (선택사항)"
                      className="w-full px-4 py-2 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-secondary-600 hover:bg-secondary-700 text-white font-medium text-sm transition-all duration-200 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-bookmark-line"></i>
                      {saving ? '저장 중...' : '번호 저장하기'}
                    </button>
                    {saveError && (
                      <p className="text-xs text-accent-600">{saveError}</p>
                    )}
                    {!user && (
                      <p className="text-xs text-foreground-400">
                        저장하려면 <button onClick={() => navigate('/login')} className="text-primary-600 underline cursor-pointer">로그인</button>이 필요합니다.
                      </p>
                    )}
                  </>
                )}
                <button
                  onClick={() => setQrModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-background-300 text-foreground-600 hover:bg-background-100 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-qr-code-line"></i>
                  QR 공유
                </button>
              </div>

              {qrModal && (
                <QRShare numbers={numbers} bonus={bonus} onClose={() => setQrModal(false)} />
              )}
            </div>
          )}
        </div>

        <div className="text-center">
          <button
            onClick={generateNumbers}
            disabled={isGenerating || cannotGenerate}
            className="
              inline-flex items-center gap-2 px-8 py-4 rounded-full
              bg-primary-500 hover:bg-primary-600
              text-white font-bold text-lg
              transition-all duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              whitespace-nowrap cursor-pointer
            "
          >
            <i className="ri-refresh-line text-xl"></i>
            {showResult ? '다시 생성하기' : '번호 생성하기'}
          </button>
        </div>
      </Reveal>

      <ResponsibleNotice />
    </section>
  );
};

export default RandomGenerator;